import React, { useState } from 'react';
import thresholds from '../data/sepsisThresholds.json';
import { AgeBucketId } from '../types';
import './ThresholdsCard.scss';

type ThresholdsCardProps = {
  selectedAgeBucketId: AgeBucketId | null;
  onAgeChange: (value: AgeBucketId | null) => void;
  temperature: number | null;
  heartRate: number | null;
  bloodPressure: number | null;
};

const ThresholdsCard: React.FC<ThresholdsCardProps> = ({
  selectedAgeBucketId,
  onAgeChange,
  temperature,
  heartRate,
  bloodPressure,
}) => {
  const [expanded, setExpanded] = useState(false);

  const buckets = thresholds.ageBuckets;
  const bucket = buckets.find((b) => b.id === selectedAgeBucketId) ?? null;

  const hyperBreached = bucket && temperature != null && temperature >= bucket.tempHyper;
  const hypoBreached  = bucket && temperature != null && temperature <= bucket.tempHypo;
  const hrBreached    = bucket && heartRate   != null && heartRate   >  bucket.hrMax;
  const bpBreached    = bucket && bloodPressure != null && bloodPressure < bucket.bpMin;

  return (
    <div className="thresholds-card" dir="rtl">
      <button
        type="button"
        className="thresholds-card__title-row"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <h3 className="thresholds-card__title">ערכים לפי גיל</h3>
        <span className="thresholds-card__chevron" aria-hidden>▾</span>
      </button>

      <div className="thresholds-card__body" data-expanded={expanded}>
        <div className="thresholds-card__body-inner">
        <div className="thresholds-card__header">
          <label className="thresholds-card__label" htmlFor="thresholds-age">גיל</label>
          <select
            id="thresholds-age"
            className="thresholds-card__select"
            value={selectedAgeBucketId ?? ''}
            onChange={(e) =>
              onAgeChange(e.target.value ? (e.target.value as AgeBucketId) : null)
            }
            data-testid="sepsis-age"
          >
            <option value="">בחר גיל</option>
            {buckets.map((b) => (
              <option key={b.id} value={b.id}>{b.label}</option>
            ))}
          </select>
        </div>

        {!bucket ? (
          <div className="thresholds-card__empty">
            בחר גיל כדי לראות ערכים מומלצים
          </div>
        ) : (
          <ul className="thresholds-card__list" data-testid="sepsis-thresholds-list">
            <li className="thresholds-card__row">
              <span className="thresholds-card__row-label">היפרתרמיה (חום מעל):</span>
              <span
                className={`thresholds-card__value${hyperBreached ? ' thresholds-card__value--breached' : ''}`}
                data-testid="sepsis-thresholds-tempHyper"
              >
                ≥ {bucket.tempHyper}°C
              </span>
            </li>
            <li className="thresholds-card__row">
              <span className="thresholds-card__row-label">היפותרמיה (חום מתחת):</span>
              <span
                className={`thresholds-card__value${hypoBreached ? ' thresholds-card__value--breached' : ''}`}
                data-testid="sepsis-thresholds-tempHypo"
              >
                ≤ {bucket.tempHypo}°C
              </span>
            </li>
            <li className="thresholds-card__row">
              <span className="thresholds-card__row-label">דופק מקסימלי (טכיקרדיה):</span>
              <span
                className={`thresholds-card__value${hrBreached ? ' thresholds-card__value--breached' : ''}`}
                data-testid="sepsis-thresholds-hrMax"
              >
                &gt; {bucket.hrMax} bpm
              </span>
            </li>
            <li className="thresholds-card__row">
              <span className="thresholds-card__row-label">ל"ד סיסטולי מינימלי:</span>
              <span
                className={`thresholds-card__value${bpBreached ? ' thresholds-card__value--breached' : ''}`}
                data-testid="sepsis-thresholds-bpMin"
              >
                &lt; {bucket.bpMin} mmHg
              </span>
            </li>
          </ul>
        )}
        </div>
      </div>
    </div>
  );
};

export default ThresholdsCard;
